import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import ChartDesignerBreakdown from 'scoreboard/charts/model/chart-designer-breakdown'
import dropdown from 'scoreboard/components/dropdown'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import axesText from './axes-text'
import template from './edit-breakdown-view.hbs'
import validBreakdownFieldIds from './valid-breakdown-field-ids'
import breakdownLabel from '../breakdown-label'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import icon from 'scoreboard/components/icon'
import TooltipAndDetails from '../../components/tooltip-and-details'

const SAME_FIELD = 'same-field'
const DIFFERENT_FIELDS = 'different-fields'
const RANGE_WARNING_THRESHOLD = 25

const EditBreakdownView = Backbone.View.extend({

	className: 'edit-breakdown-view',

	tagName: 'form',

	/**
	 * @type ChartDesignerBreakdown
	 */
	model: null,

	/**
	 * @type ChartDesigner
	 */
	chart: null,

	events: {
		'change .field-identifier': 'onChangeFieldIdentifier',
		'change [name=breakdownBy]': 'onChangeBreakdownBy'
	},

	/**
	 * @param {Object} options
	 *
	 * @param {ChartDesigner} chart
	 *
	 * @param {Dataset} [dataset] - if a breakdown on a dataset field, the dataset
	 *
	 * @param {Boolean} [isNewBreakdown] - if the breakdown is newly created. This
	 * is not the same as Backbone.Model.isNew(), because that relies on whether
	 * there is an ID (ie it has been saved to the database).
	 * options.isNewBreakdown should be true if the breakdown hasn't been created
	 * in the UI yet (like when clicking the "More Options" button)
	 */
	initialize: function(options = {}) {
		this.options = options

		this.chart = options.chart
		this.dataset = options.dataset
		this.canSelectSameField = this.dataset && !this.chart.hasMultipleDatasetSources()

		this.formView = new FormView({
			el: this.el,
			submit: this.done.bind(this),
			test: () => this.test(),
			allowCleanSubmission: this.options.isNewBreakdown
		})

		this.previousBreakdown = this.options.isNewBreakdown
			? null : _.cloneDeep(this.model.toPostJSON())

		this.firstRender = true
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		this.renderToggle()
		this.renderBreakdownBy()

		this.formView.start()

		this.firstRender = false

		return this
	},

	renderToggle: function() {
		const series = this.getDatasetSeries()
		let aggregationCount = 0
		if (series.length) {
			const source = series[0].get('source')
			aggregationCount = _.get(source, 'aggregation.aggregations.length', 0)
		}
		const hideToggle = series.length <= 1 && aggregationCount <= 1
		this.$('.toggle').toggleClass('is-not-displayed', hideToggle)
		if (hideToggle) {
			this.renderDefaultFieldIdentifier()
			return
		}

		const toggleData = [
			{ label: i18n.get('charts.breakdown.sameField'), value: SAME_FIELD },
			{ label: i18n.get('charts.breakdown.differentFields'), value: DIFFERENT_FIELDS }
		]
		this.toggle = new LabeledToggle({ options: toggleData })
		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()

		const datasetFields = this.model.get('datasetFields')
		const currentValue = this.dataset && (_.isEmpty(datasetFields) || _.some(datasetFields,
			f => !f.serieId && !f.serieTempId)) ? SAME_FIELD : DIFFERENT_FIELDS
		this.toggle.val(currentValue, { silent: false })

		this.$(`.toggle-container .option[data-value="${SAME_FIELD}"]`)
			.toggleClass('is-disabled', !this.canSelectSameField)
		this._noRepeatBySameFieldTooltipTooltip()

		this.toggle.on('change', this.onDatasetToggleChanged.bind(this))
		this.onDatasetToggleChanged(currentValue, false)
	},

	_noRepeatBySameFieldTooltipTooltip: function() {
		this.noRepeatBySameFieldTooltip && this.noRepeatBySameFieldTooltip.remove()

		if (this.canSelectSameField) return

		const i18nCodeSuffix = !this.dataset ? 'calculatedSerieNoRepeat' : 'multipleDatasetsNoRepeat'
		this.noRepeatBySameFieldTooltip = new TooltipAndDetails({
			el: this.el,
			selector: `[data-value="${SAME_FIELD}"]`,
			tooltipContent: () => i18n.get(`charts.breakdown.error.${i18nCodeSuffix}`),
			tooltipOptions: {
				extraClass: 'tooltip-body-only'
			}
		})
	},

	onDatasetToggleChanged: function(selected, clear = true) {
		if (selected === SAME_FIELD && !this.canSelectSameField) {
			// If there is no starting dataset, don't allow selecting "Same Field"
			this.toggle.val(DIFFERENT_FIELDS, { silent: false })
			return
		}

		this.toggleValue = selected
		this.$('.field-identifier-section').toggleClass('is-not-displayed', selected !== SAME_FIELD)
		this.$('.different-fields-container').toggleClass('is-not-displayed', selected !== DIFFERENT_FIELDS)
		this.formView.clearErrors()
		if (clear) {
			// #43311 these need to be cleared out so the newly rendered dropdowns
			// show all the correct options
			this.model.set({
				datasetId: null,
				datasetFieldId: null,
				datasetVisibleLinkedFieldId: null
			})
		}
		if (selected === SAME_FIELD) {
			this.renderDefaultFieldIdentifier()
		} else {
			this.renderDatasetFieldIdentifiers()
		}
		this.renderBreakdownBy()
	},

	renderBanner: function() {
		let fieldIdentifier = this.selectedFieldIdentifier()

		// On initial render AND after a change, start out with the info banner
		// hidden; we'll show it if necessary later
		this.$('.information-banner').toggleClass('is-not-displayed', true)

		if (!fieldIdentifier) return

		const showBanner = (msgCode, arg = null, asWarning = false) => {
			this.$('.information-banner').toggleClass('is-not-displayed', !msgCode)

			if (msgCode) {
				this.$('.information-banner').text(i18n.get(msgCode, arg))
				this.$('.information-banner').toggleClass('banner-warning', asWarning)
			}
		}

		if (this.model.isCalendarPeriodBreakdown()) {
			showBanner('charts.breakdown.byDate.description')
		} else {
			const field = this.dataset.getFieldByFieldIdentifier(fieldIdentifier)
			if ([ 'every-value', 'every-category', 'every-range' ].includes(this.selectedBreakdownBy())) {
				// We need to check that this isn't going to create too many value
				// breakdowns

				let renderMessage = (showWarning) => {
					if (showWarning) {
						// We need to show a slightly different message for these
						// plot types
						const plotTypeQualifier = ['line', 'bar', 'area']
							.includes(this.chart.get('plotType')) ? 'values' : 'charts'
						const msgCode = `charts.breakdown.byField.warning.${plotTypeQualifier}`
						showBanner(msgCode, field.label, showWarning)
					} else {
						// #45551 If showing "every-whatever" doesn't exceed the
						// 25 count warning, we don't have a message to show
						showBanner(null)
					}
				}

				if (this.firstRender) {
					// If this is the first time rendering the form, then we already
					// know how many breakdown ranges there are
					const showWarning = this.model.ranges.length > RANGE_WARNING_THRESHOLD

					renderMessage(showWarning)
				} else {
					// Otherwise, call the range size validation XHR, which will
					// let us know if these settings cause more than 25 ranges
					// to be created
					this.validateRangeThreshold(response => {
						renderMessage(!!response.exceededThreshold)
					})
				}
			} else {
				showBanner('charts.breakdown.byField.description', field.label)
			}
		}
	},

	context: function () {
		let title, repeatBy
		// #41653 if this is a non-axes chart, don't mention axis (just "Repeat By")
		if (nonAxesPlotType(this.chart)) {
			title = i18n.get('datasets.breakdown')
			repeatBy = i18n.get('datasets.breakdown')
		} else {
			let axes = axesText(this.chart)
			title = i18n.get('charts.breakdown.repeat', axes)
			repeatBy = i18n.get('charts.breakdown.repeatBy', axes)
		}
		return {
			title,
			repeatBy
		}
	},

	renderDefaultFieldIdentifier: function() {
		const dataset = this.dataset
		if (!this.dataset) {
			// Only show field selector if there is a dataset source on the chart
			this.$('.field-identifier-section').addClass('is-not-displayed')
			return
		}

		let selectedId = this.model.getDatasetFieldIdentifier({ dataset, serieId: null, serieTempId: null })
		this.renderFieldIdentifier(dataset, 'fieldIdentifier', selectedId, false)
	},

	renderFieldIdentifier: function (dataset, selectName, selectedId, dateFieldsOnly = false) {
		let validFieldIds = validBreakdownFieldIds({
			chart: this.chart,
			dataset,
			breakdown: this.model,
			dateFieldsOnly
		})

		this.dropdown = renderFieldDropdown({
			el: this.fieldIdentifierInput(selectName),
			required: true,
			dataset,
			selectedId,
			allowedFieldIds: validFieldIds.fieldIds,
			allowedVisibleLinkedFieldIds: validFieldIds.visibleLinkedFieldIds,
			filterVisibleLinkedFields: true
		})
	},

	renderDatasetFieldIdentifiers: function() {
		const series = this.getDatasetSeries()
		const container = this.$('.different-fields-container')
		container.html('')
		let fieldCount = 0
		_.forEach(series, (s, i) => {
			const label = _.escape(this.chart.featureLabel(
				{ feature: s, id: s.id, tempId: s.tempId}))

			const agg = s.get('source').aggregation
			if (agg.dataSourceType === 'basic' || agg.aggregations.length <= 1) {
				fieldCount = this._renderNonAggregatedField(fieldCount, s, label, container)
			} else {
				fieldCount = this._renderAggregatedField(i, fieldCount, s, agg, label, container)
			}
		})
	},

	/**
	 * For series that are set to "basic" or only have a single field in its equation
	 */
	_renderNonAggregatedField: function(fieldCount, serie, label, container) {
		const fieldName = `fieldIdentifier${fieldCount + 1}`
		const html = `
			<label class="full-width">
				<span class="label-text">${i18n.get('charts.breakdown.repeatDatasetBy', label)}</span>
				<select name="${fieldName}" ${this._getSerieIdHtml(serie)} class="field-identifier"></select>
			</label>`

		container.append(html)
		const dataset = this.chart.getDatasetForDatasetSeries(serie)
		const breakdownField = _.find(this.model.get('datasetFields'), f =>
			(serie.get('id') && f.serieId === serie.get('id')) || (serie.get('tempId') && f.serieTempId === serie.get('tempId'))
		)
		const selectedId = breakdownField ? this.model.getDatasetFieldIdentifier(
			{ dataset, serieId: breakdownField.serieId, serieTempId: breakdownField.serieTempId }) : null
		this.renderFieldIdentifier(dataset, fieldName, selectedId, true)

		return fieldCount + 1
	},

	/**
	 * For series set to "calculated" and that have multiple fields in their equation
	 */
	_renderAggregatedField: function(index, fieldCount, serie, aggregation, label, container) {
		const containerClass = `breakdown-aggregations-container-${index}`
		let html = `
			<label class="full-width ${containerClass}">
				<span class="label-text">${i18n.get('charts.breakdown.repeatCalculatedField', label)}</span>
			</label>`
		container.append(html)

		let aggFieldCount = fieldCount + 1
		_.forEach(aggregation.aggregations, (a, i) => {
			const fieldName = `fieldIdentifier${aggFieldCount++}`
			const dataset = this.chart.data.datasetModel(a.datasetId)
			const field = dataset.combinedFieldsById[a.fieldId]
			const fieldLabel = field ? field.label : i18n.get('datasets.total.tracked', dataset.get('tracked'))
			// Wrap in div so margin-collapsing works correctly
			html = `
				<div>
					<div class="aggregation-breakdown-by down-right-option">
						${icon('down-right-arrow')}
						<label>
							<span class="label-text">
								${i18n.get('charts.breakdown.repeatAggregationBy', _.escape(dataset.get('label')), _.escape(fieldLabel))}
							</span>
							<select name="${fieldName}" ${this._getSerieIdHtml(serie)} data-aggid="${i}" class="field-identifier"></select>
						</label>
					</div>
				</div>`

			this.$(`.${containerClass}`).parent().append(html)

			const breakdownField = _.find(this.model.get('datasetFields'), f => {
				const { id, tempId } = serie.attributes
				// Look for a matching serie id OR tempId AND aggregation sort order
				return ((id && f.serieId === id) || (tempId && f.serieTempId === tempId))
					&& f.datasetAggregationSortOrder === i
			})
			const selectedId = breakdownField ? this.model.getDatasetFieldIdentifier(
				{ dataset, serieId: breakdownField.serieId,
					serieTempId: breakdownField.serieTempId, aggSortOrder: breakdownField.datasetAggregationSortOrder }) : null
			this.renderFieldIdentifier(dataset, fieldName, selectedId, true)
		})

		return aggFieldCount
	},

	_getSerieIdHtml: function(serie) {
		let dataSerieIds = ''
		if (serie.get('id')) {
			dataSerieIds += ` data-serieid="${serie.get('id')}"`
		}
		if (serie.get('tempId')) {
			dataSerieIds += ` data-serietempid="${serie.get('tempId')}"`
		}
		return dataSerieIds
	},

	onChangeFieldIdentifier: function (event) {
		const serieId = $(event.currentTarget).data('serieid')
		const serieTempId = $(event.currentTarget).data('serietempid')
		const elementName = $(event.currentTarget).attr('name')
		const fieldIdentifier = this.selectedFieldIdentifier(elementName)
		const aggSortOrder = $(event.currentTarget).data('aggid')
		const serie = this.chart.getSerieFeatures().find(f => {
			const { id, tempId } = f.attributes
			return (id && id == serieId) || (tempId && tempId == serieTempId)
		})
		let datasetId
		if (serie) {
			const source = serie.get('source')
			if (source.aggregation.dataSourceType == 'calculated') {
				// Get the dataset id from the aggregations
				datasetId = _.get(source, `aggregation.aggregations[${aggSortOrder || 0}].datasetId`)
			} else {
				datasetId = _.get(source, 'aggregation.startingDatasetId')
			}
		}
		const dataset = datasetId ? this.chart.data.datasetModel(datasetId) : this.dataset
		this.model.setDatasetFieldIdentifier({
			chartDesigner: this.chart,
			fieldIdentifier,
			dataset,
			serieId: typeof serieId === 'number' ? serieId : null,
			serieTempId: serieTempId && serieTempId.length ? serieTempId : null,
			aggSortOrder
		})

		// Picking a new field could case the breakdownBy options and value
		// to change, so re-render the dropdown
		this.renderBreakdownBy()
	},

	fieldIdentifierInput: function (name) {
		return this.$(`[name=${name}]`)
	},

	selectedFieldIdentifier: function (name = 'fieldIdentifier') {
		return this.fieldIdentifierInput(name).val() || null
	},

	_isCalendarBreakdown: function() {
		const dataType = this.model.fieldDataType(this.chart)
		let isCalendarBreakdown = ChartDesignerBreakdown.isCalendarPeriodBreakdownType(dataType)

		if (this.toggleValue === SAME_FIELD) {
			// #41247 check the select field in case the user switched back and
			// forth on the toggle without actually updating anything
			const fieldIdentifier = this.selectedFieldIdentifier()

			isCalendarBreakdown =
				// If no field has been selected, can't show breakdownBy dropdown yet
				_.isNil(fieldIdentifier)
				// If a date field has been selected, the two valid breakdownBy options
				// ('calendar-period-ranges' and 'calendar-period-grouped-ranges')
				// are set from the Edit Range dialog instead
				|| this.model.isCalendarPeriodBreakdown()

		}
		return isCalendarBreakdown
	},

	renderBreakdownBy: function () {
		const breakdownBy = this.model.get('breakdownBy')
		const dataType = this.model.fieldDataType(this.chart)
		let hideBreakdownBy =
			// calendar fields don't have a breakdownBy selection here (that
			// can be changed in the Edit Range dialog)
			this._isCalendarBreakdown()
			// If no fields have been selected yet, we can't determine what
			// options should be in breakdownBy
			|| _.isEmpty(this.model.get('datasetFields'))
			// If we can't use the "same field" tab then the only valid options
			// are date fields
			|| !this.canSelectSameField
			// #46866 don't show the breakdown by select if the toggle is set to
			// "different fields"
			|| this.toggleValue == DIFFERENT_FIELDS

		this.$('.breakdown-by-section').toggleClass('is-not-displayed', hideBreakdownBy)
		if (hideBreakdownBy) {
			this.renderBanner()
			return
		}

		const labelOptions = {
			domainData: this.chart.data,
			useGenericDataType: dataType
		}

		let allowFieldRanges = false
		if (this.model.usingSingleFieldBreakdown()) {
			const fieldIdentifier = this.selectedFieldIdentifier()
			const ids = this.dataset.fieldIdentifierToIds(fieldIdentifier)

			labelOptions.datasetId = this.dataset.id
			labelOptions.useGenericDataType = null

			if (ids) {
				labelOptions.datasetFieldId = ids.fieldId
				labelOptions.datasetVisibleLinkedFieldId = ids.datasetVisibleLinkedFieldId
				let field = ids.fieldId ? this.dataset.combinedFieldsById[ids.fieldId] : null
				if (field) {
					allowFieldRanges = !!field.ranges
				}
			}
		}

		const options = ChartDesignerBreakdown.validBreakdownByDataType(dataType, allowFieldRanges).map(val => {
			return {
				id: val,
				label: breakdownLabel(_.extend(labelOptions,{
					breakdownBy: val
				}))
			}
		})

		dropdown.idLabels(this.breakdownByInput().empty(), options, breakdownBy)
		this.renderBanner()
	},

	onChangeBreakdownBy: function () {
		this.model.set('breakdownBy', this.selectedBreakdownBy())
		this.renderBanner()
	},

	breakdownByInput: function () {
		return this.$('[name=breakdownBy]')
	},

	selectedBreakdownBy: function () {
		return this.breakdownByInput().val()
	},

	getDatasetSeries: function() {
		return this.chart.getSerieFeatures()
			.filter(s => s.get('source').domainType === 'dataset')
	},

	test: function() {
		// Find all visible selects and ensure they have a value set
		const selects = this.$('select.field-identifier:visible')
		if (selects.length && _.some(selects, s => _.isEmpty($(s).val()))) {
			return i18n.get('charts.breakdown.allFieldsRequired')
		}

		if (!this.model.fieldDataType(this.chart)) {
			return  i18n.get('charts.breakdown.allDatatypesMustBeTheSame')
		}
	},

	validateBreakdown: function (callback) {
		$.ajax({
			method: 'POST',
			url: `api/charts/validate-breakdown`,
			data: JSON.stringify(this.validationData()),
			success: callback
		})
	},

	validateRangeThreshold: function (callback) {
		// Need a brief delay, or the FormView's validation state handling will
		// undo our call to loading() (because it passes validation) and the
		// user will never see the loading dots
		setTimeout(() => {
			let data = _.omit(this.validationData(), 'previousBreakdown')
			data.threshold = RANGE_WARNING_THRESHOLD

			this.formView.getPrimaryButton().loading()

			$.ajax({
				method: 'POST',
				url: `api/charts/validate-range-threshold`,
				data: JSON.stringify(data),
				success: response => {
					this.formView.getPrimaryButton().toggle(true)
					callback(response)
				}
			})
		}, 10)
	},

	validationData: function () {
		if (!this.model.get('breakdownBy') && this._isCalendarBreakdown()) {
			// Provide a sensible default for the breakdown by if one was not
			// selected. (The breakdown by select is not displayed if we are breaking
			// down by date)
			this.model.set('breakdownBy', 'calendar-period-ranges')
		}

		return {
			chartDesigner: this.chart.toPostJSON(),
			breakdown: this.model.toPostJSON(),
			previousBreakdown: this.previousBreakdown
		}
	},

	done: function () {
		this.validateBreakdown(updatedDesigner => {
			this.trigger('done', updatedDesigner)
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.formView, this.toggle, this.noRepeatBySameFieldTooltip ], 'remove')
	}

})

export default EditBreakdownView
export { RANGE_WARNING_THRESHOLD }

EditBreakdownView.__filename__ = 'charts/edit/edit-breakdown-view.js'
